import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import { ArrowLeft, Users, X } from "lucide-react";
import { Seo } from "@/components/Seo";


export default function ProfessionalAudience() {
  const navigate = useNavigate();
  const [audience, setAudience] = useState([]);
  const [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const loadCount = async () => {
    try {
      const r = await api.get(`/professional/audience/count`);
      setTotal(r.data.count || 0);
    } catch {
      /* ignore */
    }
  };

  const loadPage = useCallback(async (nextCursor) => {
    if (loading) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "30" });
      if (nextCursor) params.set("cursor", nextCursor);
      const r = await api.get(`/professional/audience?${params.toString()}`);
      setAudience((prev) => nextCursor ? [...prev, ...(r.data.audience || [])] : (r.data.audience || []));
      setCursor(r.data.next_cursor);
      if (!r.data.next_cursor) setDone(true);
    } catch (e) {
      if (e?.response?.status === 403) {
        navigate("/app/settings/professional");
      }
    } finally {
      setLoading(false);
    }
  }, [loading, navigate]);

  useEffect(() => { loadCount(); loadPage(null); }, [loadPage]);

  const removeFan = async (fanId) => {
    if (!window.confirm("Rimuovere questo fan dalla tua audience?")) return;
    setBusyId(fanId);
    try {
      await api.delete(`/professional/audience/${fanId}`);
      setAudience((prev) => prev.filter((a) => a.user.user_id !== fanId));
      setTotal((n) => Math.max(0, n - 1));
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="professional-audience-page">
      <Seo title="Audience · ADE" description="Le persone collegate al tuo profilo ADE Pro" />
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b flex items-center gap-3 px-4 py-3">
        <button onClick={() => navigate(-1)} className="p-1" data-testid="audience-back">
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="text-sm font-semibold text-gray-800 flex-1">Audience</h1>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-200" data-testid="audience-total">
          <Users className="w-3 h-3" /> {total}
        </span>
      </div>

      {!loading && audience.length === 0 && done && (
        <div className="p-8 text-center" data-testid="audience-empty">
          <p className="text-gray-500 text-sm">La tua audience è ancora vuota. Condividi il link del tuo profilo per attirare i primi collegamenti.</p>
        </div>
      )}

      <div className="px-4 py-3 space-y-2">
        {audience.map((a) => (
          <div
            key={a.id}
            className="w-full flex items-center gap-3 p-3 rounded-2xl border border-gray-100 hover:bg-gray-50"
            data-testid={`audience-item-${a.user.user_id}`}
          >
            <button
              onClick={() => navigate(`/app/user/${a.user.user_id}`)}
              className="flex items-center gap-3 flex-1 min-w-0 text-left"
            >
              <img
                src={a.user.picture || "/images/default-avatar.png"}
                alt=""
                className="w-11 h-11 rounded-full object-cover bg-gray-100"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{a.user.name}</p>
                <p className="text-[11px] text-gray-500">
                  Collegato {new Date(a.created_at).toLocaleDateString("it-IT")}
                  {a.source ? ` · ${a.source.replace(/_/g, " ")}` : ""}
                </p>
              </div>
            </button>
            <button
              onClick={() => removeFan(a.user.user_id)}
              disabled={busyId === a.user.user_id}
              className="p-2 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-500 disabled:opacity-40"
              title="Rimuovi dall'audience"
              data-testid={`audience-remove-${a.user.user_id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {cursor && !done && (
        <div className="text-center py-3">
          <button
            onClick={() => loadPage(cursor)}
            disabled={loading}
            className="px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-medium disabled:opacity-50"
            data-testid="audience-load-more"
          >
            {loading ? "…" : "Carica altri"}
          </button>
        </div>
      )}
    </div>
  );
}
